import { useState, useRef } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Mail, Phone, MapPin, Send, CheckCircle2 } from "lucide-react";
import AnimatedBackground from "@/components/AnimatedBackground";
import { showSuccessToast } from "@/components/SuccessToast";
import { formService } from "@/services/formService";
import { useFormSubmission } from "@/hooks/use-form-submission";

const Contact = () => {
  const [formData, setFormData] = useState({
    user_name: "",
    user_email: "",
    company: "",
    phone: "",
    subject: "",
    message: "",
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const formRef = useRef<HTMLFormElement>(null);

  const { isLoading: isSubmitting, handleSubmit: submitForm } = useFormSubmission({
    submitFn: async (data: any) => {
      await formService.submitDemoRequest({
        name: data.user_name,
        email: data.user_email,
        company: data.company,
        phone: data.phone,
        subject: data.subject,
        message: data.message,
      });
    },
    onSuccess: () => {
      setIsSubmitted(true);
      showSuccessToast(formData.user_name, 'contact');
      setFormData({
        user_name: "",
        user_email: "",
        company: "",
        phone: "",
        subject: "",
        message: "",
      });
      setTimeout(() => setIsSubmitted(false), 3000);
    }
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setSubmitError("");
  };

  const validateForm = (): boolean => {
    if (!formData.user_name.trim()) {
      setSubmitError("Please enter your name");
      return false;
    }
    if (!formData.user_email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.user_email)) {
      setSubmitError("Please enter a valid email address");
      return false;
    }
    if (!formData.subject.trim()) {
      setSubmitError("Please enter a subject");
      return false;
    }
    if (!formData.message.trim()) {
      setSubmitError("Please enter a message");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    await submitForm(formData);
  };

  return (
    <div className="min-h-screen bg-tasktide-bg dark:bg-gray-900 font-sans flex flex-col relative overflow-hidden">
      {/* Animated background */}
      <AnimatedBackground variant="mesh" className="absolute inset-0 -z-10 opacity-50 dark:opacity-30" />
      
      <Header />

      <main className="flex-1 relative z-10 pt-24">
        {/* Hero Section */}
        <section className="pt-8 pb-20 px-4 text-center animate-fade-in">
          <h1 className="text-5xl md:text-6xl font-extrabold text-tasktide-dark dark:text-white mb-6 gradient-text">
            Get in Touch
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">
            Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </p>
        </section>

        <section className="pb-20 px-4 md:px-8 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="space-y-8 animate-slide-in-left">
              <div className="bg-white/50 dark:bg-gray-800/50 backdrop-blur-xl p-8 rounded-[2rem] border border-optibiz-blue/20 hover:border-optibiz-blue/30 dark:hover:border-optibiz-blue/50 transition-all duration-300 md:hover:scale-105">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-optibiz-blue to-blue-600 flex items-center justify-center text-white mb-6 shadow-lg">
                  <Mail size={24} />
                </div>
                <h3 className="text-xl font-bold text-tasktide-dark dark:text-white mb-2">Email Us</h3>
                <p className="text-gray-500 dark:text-gray-400 mb-4">Send us an email anytime</p>
                <a href="mailto:info@optibiz.co.ke" className="text-optibiz-blue font-bold hover:underline transition-colors">info@optibiz.co.ke</a>
              </div>

              <div className="bg-white/50 dark:bg-gray-800/50 backdrop-blur-xl p-8 rounded-[2rem] border border-optibiz-blue/20 hover:border-optibiz-green/30 dark:hover:border-optibiz-green/50 transition-all duration-300 md:hover:scale-105">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-optibiz-green to-emerald-600 flex items-center justify-center text-white mb-6 shadow-lg">
                  <Phone size={24} />
                </div>
                <h3 className="text-xl font-bold text-tasktide-dark dark:text-white mb-2">Call Us</h3>
                <p className="text-gray-500 dark:text-gray-400 mb-4">Mon-Fri from 8am to 5pm</p>
                <a href="tel:+254769653431" className="text-optibiz-green font-bold hover:underline transition-colors">+254 769 653 431</a>
              </div>

              <div className="bg-white/50 dark:bg-gray-800/50 backdrop-blur-xl p-8 rounded-[2rem] border border-optibiz-blue/20 hover:border-optibiz-orange/30 dark:hover:border-optibiz-orange/50 transition-all duration-300 md:hover:scale-105">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-optibiz-orange to-orange-600 flex items-center justify-center text-white mb-6 shadow-lg">
                  <MapPin size={24} />
                </div>
                <h3 className="text-xl font-bold text-tasktide-dark dark:text-white mb-2">Visit Us</h3>
                <p className="text-gray-500 dark:text-gray-400">
                  Daima Towers, Eldoret,<br />Uasin Gishu, Kenya
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2 bg-white/50 dark:bg-gray-800/50 backdrop-blur-xl p-8 md:p-12 rounded-[1.5rem] border border-optibiz-blue/20  animate-slide-in-right">
              {isSubmitted ? (
                <div className="text-center py-20 animate-fade-in">
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-optibiz-blue to-blue-600 flex items-center justify-center text-white shadow-xl">
                    <CheckCircle2 size={40} />
                  </div>
                  <h3 className="text-3xl font-bold text-tasktide-dark dark:text-white mb-4">Message Sent</h3>
                  <p className="text-gray-600 dark:text-gray-300 text-lg mb-2">Thank you for reaching out.</p>
                  <p className="text-gray-500 dark:text-gray-400 text-base mb-8">We have received your message and will get back to you as soon as possible.</p>
                  <Button
                    onClick={() => setIsSubmitted(false)}
                    className="mt-8 bg-optibiz-blue hover:bg-optibiz-blue/90 text-white rounded-full px-8 py-6 font-bold shadow-lg transition-all md:hover:scale-105"
                  >
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2 group">
                      <label className="text-sm font-bold text-tasktide-dark dark:text-white ml-1 block">Full Name</label>
                      <Input
                        name="user_name"
                        value={formData.user_name}
                        placeholder="John Doe"
                        className="rounded-xl border-gray-200 dark:border-gray-700 dark:bg-gray-900 dark:text-white py-6 px-4 focus:border-optibiz-blue focus:ring-optibiz-blue/20 group-hover:border-optibiz-blue/40 transition-all"
                        onChange={handleChange}
                      />
                    </div>
                    <div className="space-y-2 group">
                      <label className="text-sm font-bold text-tasktide-dark dark:text-white ml-1 block">Email Address</label>
                      <Input
                        name="user_email"
                        value={formData.user_email}
                        type="email"
                        placeholder="john@example.com"
                        className="rounded-xl border-gray-200 dark:border-gray-700 dark:bg-gray-900 dark:text-white py-6 px-4 focus:border-optibiz-blue focus:ring-optibiz-blue/20 group-hover:border-optibiz-blue/40 transition-all"
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2 group">
                      <label className="text-sm font-bold text-tasktide-dark dark:text-white ml-1 block">Company (Optional)</label>
                      <Input
                        name="company"
                        value={formData.company}
                        placeholder="Your Company"
                        className="rounded-xl border-gray-200 dark:border-gray-700 dark:bg-gray-900 dark:text-white py-6 px-4 focus:border-optibiz-blue focus:ring-optibiz-blue/20 group-hover:border-optibiz-blue/40 transition-all"
                        onChange={handleChange}
                      />
                    </div>
                    <div className="space-y-2 group">
                      <label className="text-sm font-bold text-tasktide-dark dark:text-white ml-1 block">Phone (Optional)</label>
                      <Input
                        name="phone"
                        value={formData.phone}
                        type="tel"
                        placeholder="+254 700 000 000"
                        className="rounded-xl border-gray-200 dark:border-gray-700 dark:bg-gray-900 dark:text-white py-6 px-4 focus:border-optibiz-blue focus:ring-optibiz-blue/20 group-hover:border-optibiz-blue/40 transition-all"
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="space-y-2 group">
                    <label className="text-sm font-bold text-tasktide-dark dark:text-white ml-1 block">Subject</label>
                    <Input
                      name="subject"
                      value={formData.subject}
                      placeholder="How can we help?"
                      className="rounded-xl border-gray-200 dark:border-gray-700 dark:bg-gray-900 dark:text-white py-6 px-4 focus:border-optibiz-blue focus:ring-optibiz-blue/20 group-hover:border-optibiz-blue/40 transition-all"
                      onChange={handleChange}
                    />
                  </div>

                  <div className="space-y-2 group">
                    <label className="text-sm font-bold text-tasktide-dark dark:text-white ml-1 block">Message</label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      placeholder="Tell us about your project..."
                      className="rounded-xl border-gray-200 dark:border-gray-700 dark:bg-gray-900 dark:text-white min-h-[200px] p-4 focus:border-optibiz-blue focus:ring-optibiz-blue/20 group-hover:border-optibiz-blue/40 transition-all resize-none"
                      onChange={handleChange}
                    />
                  </div>

                  {submitError && (
                    <div className="p-4 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-lg">
                      <p className="text-red-700 dark:text-red-300 text-sm font-medium">{submitError}</p>
                    </div>
                  )}

                  <Button
                    type="submit"
                    className=" bg-gradient-to-r from-optibiz-blue to-optibiz-green hover:from-optibiz-blue/90 hover:to-optibiz-green/90 dark:from-optibiz-orange dark:to-optibiz-yellow text-white rounded-lg py-5 text-md font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Sending your message...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        Send Message
                        <Send className="w-5 h-5" />
                      </span>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Contact;
